import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from 'react-responsive';

import ActivityBlockCard, {
  ActivityBlockCardProps,
  ActivityInlineFormProps,
} from '@/components/activity/ActivityBlockCard';
import QuestionsList from '@/components/activity/QuestionsList';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import { Badge } from '@/components/ui/daisy';
import { ExamActivityVersioningProvider } from '@/data/context/ExamActivityVersioningProvider';
import {
  QuestionExamGradeProvider,
  useQuestionExamGradeCtx,
} from '@/data/context/QuestionExamGradeContext';
import useExamQuestionBreadcrumb from '@/data/hook/exam/useExamQuestionBreadcrumb';
import { ListCacheManager } from '@/data/hook/query/v2/CacheFacade';
import { VersionedActivityArgs } from '@/data/hook/exam/useExamActivities';
import Activity from '@/models/Activity';
import Exam from '@/models/Exam';
import ExamArea from '@/models/ExamArea';
import { Breakpoints } from '@/utils/Breakpoint';
import { CourseAdminCard } from '@/components/shared/CourseAdminCard';

import ExamBlockBadges from './ExamBlockBadges';
import ExamBlockEditForm from './ExamBlockEditForm';
import usePedWorkspace from '@/data/hook/ped-content/usePedWorkspace';

type ExamActivityRowProps = Pick<
  ActivityBlockCardProps,
  'activity' | 'isUpdating'
> & {
  examArea: ExamArea;
  exam: Exam;
  examAreas: ExamArea[];
  cacheManager: ListCacheManager<Activity>;
  onUpdate: (params: {
    examAreaId: number;
    activityId: number;
    data: Partial<Activity>;
  }) => Promise<Activity>;
  onDelete: (params: { examAreaId: number; activityId: number }) => void;
  onVersioned: (params: VersionedActivityArgs) => void;
  isDraggable?: boolean;
  isDragging?: boolean;
  isReordering?: boolean;
  index: number;
};

export default function ExamActivityRow({
  activity,
  examArea,
  exam,
  examAreas,
  cacheManager,
  isUpdating,
  index,
  onUpdate,
  onDelete,
  onVersioned,
  isDraggable,
  isDragging,
  isReordering,
}: ExamActivityRowProps) {
  const InlineForm = useCallback(
    (props: ActivityInlineFormProps) => (
      <ExamBlockEditForm
        {...props}
        exam={exam}
        examAreas={examAreas}
        examAreaId={examArea.id}
      />
    ),
    [exam, examAreas, examArea.id],
  );

  const breadcrumbItems = useExamQuestionBreadcrumb({ activity });

  const dragProps = isDraggable
    ? {
        isDraggable: true as const,
        id: `activity-${activity.id}`,
        index,
        disabled: isReordering,
      }
    : { isDraggable: false as const };

  const { isLimitedEdition } = usePedWorkspace();

  return (
    <QuestionExamGradeProvider examAreaId={examArea.id}>
      <ActivityBlockCard
        className="mb-6"
        activity={activity}
        isUpdating={isUpdating}
        onUpdate={({ activityId, data }) =>
          onUpdate({ examAreaId: examArea.id, activityId, data })
        }
        onDelete={activityId =>
          onDelete({ examAreaId: examArea.id, activityId })
        }
        leftAddon={
          <ExamBlockBadges
            examArea={examArea}
            shuffleQuestions={activity.shuffleQuestions}
          />
        }
        actionsAddon={<ActivityPointsBadge activity={activity} />}
        InlineForm={InlineForm}
        isDragging={isDragging}
        isLimitedEdition={isLimitedEdition}
        {...dragProps}
      >
        <CourseAdminCard.NestedList>
          <ExamActivityVersioningProvider
            activity={activity}
            examAreaId={examArea.id}
            onVersioned={(versioned, previous) =>
              onVersioned({
                examAreaId: examArea.id,
                previousId: previous.id,
                versionedId: versioned.id,
              })
            }
          >
            <QuestionsList
              activity={activity}
              cacheManager={cacheManager}
              isDraggable
              breadcrumbItems={breadcrumbItems}
              isLimitedEdition={isLimitedEdition}
            />
          </ExamActivityVersioningProvider>
        </CourseAdminCard.NestedList>
      </ActivityBlockCard>
    </QuestionExamGradeProvider>
  );
}

type ActivityPointsBadgeProps = {
  activity: Activity;
};

function ActivityPointsBadge({ activity }: ActivityPointsBadgeProps) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'courseLineForm.examWorkspace.questions',
  });
  const grade = useQuestionExamGradeCtx();
  const total = grade?.getActivityTotal(activity) ?? 0;
  const isXl = useMediaQuery({ minWidth: Breakpoints.xl });

  return (
    <ConditionalRenderer condition={!!grade && activity.questions.length > 0}>
      <Badge variant="soft" color="info" size={isXl ? 'md' : 'sm'}>
        {t('pointsCount', { count: total })}
      </Badge>
    </ConditionalRenderer>
  );
}
